import React, { useState, useEffect } from "react";
import "./index.scss";
import { getPrHeadersConfig, prFilters, PR_SCOPES } from "./helper";
import { PR_STATUSES, PR_TYPES } from "../purchase-requisition/pr-list/helper";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import {
  FINANCE_V2_HOSTNAME,
  INTRANETHOSTNAME,
  REPORT_HOSTNAME,
} from "../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../common-utilities/utils/end-points/finance-v2";
import { toast } from "react-toastify";
import MuiTable from "../../../common-utilities/core/mui-table";
import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import {
  AutoCompleteSelect,
  DateRange,
  TextFieldKeyVal,
  getLast2MonthsDateRange,
} from "./utils/utils";
import { DownloadBtn } from "../../../common-utilities/core/download-btn";
import ClearIcon from "@mui/icons-material/Clear";
import CustomModal from "../../../common-utilities/core/modal";
import { setProperty } from "../../../common-utilities/utils/misc";
import { getStartOfDay, getEndOfDay } from "../../../common-utilities/utils/time";
import { downloadFileFromUrl } from "../../../electricity-consumption/utils/common-utils";
import { getFinancialYears } from "../home/financial-year-dropdown";
import { getPrDepartmentScope } from "../../utils/common-utils";

const PR_REPORT_STATUSES = [...PR_STATUSES, { label: "Partially Rejected", value: "partially_rejected" }];

const PurchaseRequestReport = () => {
  const [prList, setPrList] = useState({});
  const [filterLabel, setFilterLabel] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    sortBy: "-createdAt",
  });
  const [categoryList, setCategoryList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [creatorList, setCreatorList] = useState([]);
  const [showLargeText, setShowLargeText] = useState({
    isOpen: false,
    text: "",
  });
  const [config, setConfig] = useState({
    showTable: true,
    approverHeaders: [],
  });

  const addSearchParams = (params) => {
    switch (filterLabel) {
      case "creationDate":
        if (
          ![null, undefined, NaN, ""].includes(filterValue?.startDate) &&
          ![null, undefined, NaN, ""].includes(filterValue?.endDate)
        ) {
          params["startDate"] = getStartOfDay(filterValue?.startDate, true);
          params["endDate"] = getEndOfDay(filterValue?.endDate, true);
          params["dateKey"] = filterLabel;
        }
        break;
      default:
        if (filterLabel && ![null, undefined, NaN, ""].includes(filterValue)) {
          params["keyName"] = filterLabel;
          params["keyValue"] = filterValue;
        }
        break;
    }
  };

  const loadData = (filters, isDownload = false) => {
    const params = { ...filters };
    if (!isDownload) {
      setProperty("showTable", false, setConfig);
    }
    invokeApi(
      HTTP_METHODS.GET,
      `${isDownload ? REPORT_HOSTNAME : FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.REPORTS}purchase_request`,
      null,
      params
    )
      .then((response) => {
        if (response.message) {
          setProperty("showTable", true, setConfig);
          toast.error(response.message, { autoClose: 2000 });
        }
        if (isDownload) {
          downloadFileFromUrl(response.url);
        } else {
          const uniqueFieldNames = new Set();
          const newResult = [];
          if (response?.results?.prData) {
            response.results = [...response.results.prData];
            response.results.forEach((item) => {
              if (item?.stages) {
                Object.keys(item.stages).forEach((fieldName) => {
                  if (fieldName.endsWith(" Department")) return;
                  uniqueFieldNames.add(fieldName);
                });
              }
              const itemStage = item?.stages || {};
              const visibleStage = Object.fromEntries(
                Object.entries(itemStage).filter(([k]) => !k.endsWith(" Department"))
              );
              delete item.stages;
              newResult.push({ ...item, ...visibleStage });
            });
          } else {
            response.results = [];
          }
          response.results = [...newResult];
          const formattedUniqueFieldNames = Array.from(uniqueFieldNames).map((fieldName) => ({
            label: fieldName,
            id: fieldName,
          }));
          setProperty("approverHeaders", formattedUniqueFieldNames, setConfig);
          setPrList(response);
          setTimeout(() => {
            setProperty("showTable", true, setConfig);
          }, 300);
        }
      })
      .catch((err) => {
        setProperty("showTable", true, setConfig);
        console.log(err);
      });
  };

  const searchHandler = () => {
    const params = { ...filters, page: 1 };
    addSearchParams(params);
    loadData(params);
  };

  const getCategoryList = () => {
    invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_CATEGORY_LIST}`)
      .then((res) => {
        setCategoryList(res.map((ele) => ({ label: ele.name, value: ele.id })));
      })
      .catch((err) => console.log(err));
  };
  const getStoreList = () => {
    invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.STORE_DATA_LIST}`, null, {
      additionalFields: "gstNumber,storeId",
      poBudgetLocations: true,
      getStoreData: true,
    })
      .then((res) => {
        setStoreList(res.map((ele) => ({ label: `${ele?.code}- ${ele?.gstNumber}`, value: ele?._id })));
      })
      .catch((err) => console.log(err));
  };
  const getSubCategoryList = () => {
    invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_SUB_CATEGORY_LIST}`)
      .then((res) => {
        setSubCategoryList(res.map((ele) => ({ label: ele.name, value: ele?._id })));
      })
      .catch((err) => console.log(err));
  };
  const getDepartmentList = () => {
    invokeApi(HTTP_METHODS.GET, `${INTRANETHOSTNAME}${FINANCE_REST_URLS.GET_HOD_DEPARTMENT_LIST}`, null, {
      page: 1,
      limit: 1000,
    })
      .then((res) => {
        const { restricted, departmentIds } = getPrDepartmentScope();
        const departments = restricted
          ? res.results.filter((ele) => departmentIds.includes(Number(ele?.id ?? ele?._id)))
          : res.results;
        setDepartmentList(departments.map((ele) => ({ label: `${ele.name}- ${ele?.head?.name || ""}`, value: ele?.id })));
      })
      .catch((err) => console.log(err));
  };
  const getItemList = () => {
    invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_ITEM_LIST}`)
      .then((res) => {
        setItemList(res.map((ele) => ({ label: ele.name, value: ele?._id })));
      })
      .catch((err) => console.log(err));
  };
  const getCreatorList = () => {
    invokeApi(HTTP_METHODS.GET, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_CREATOR_LIST}`)
      .then((res) => {
        setCreatorList(res.map((ele) => ({ label: ele?.name, value: ele?.id })));
      })
      .catch((err) => console.log(err));
  };

  const getFilterValue = (type) => {
    switch (type) {
      case "prNumber":
        return <TextFieldKeyVal setFilterValue={setFilterValue} placeHolder="Search PR Number" />;
      case "prType":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={PR_TYPES} filterLabel={filterLabel} />;
      case "prScope":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={PR_SCOPES} filterLabel={filterLabel} />;
      case "status":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={PR_REPORT_STATUSES} filterLabel={filterLabel} />;
      case "site":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={storeList} filterLabel={filterLabel} />;
      case "category":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={categoryList} filterLabel={filterLabel} />;
      case "subCategory":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={subCategoryList} filterLabel={filterLabel} />;
      case "itemId":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={itemList} filterLabel={filterLabel} />;
      case "department":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={departmentList} filterLabel={filterLabel} />;
      case "createdBy":
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={creatorList} filterLabel={filterLabel} />;
      case "financialYear": {
        const { financialYears } = getFinancialYears();
        return <AutoCompleteSelect setFilterValue={setFilterValue} optionsList={financialYears} />;
      }
      case "creationDate":
        return <DateRange setFilterValue={setFilterValue} />;
      default:
        return null;
    }
  };

  const downloadReport = () => {
    const params = { ...filters, download: true };
    params.page = 1;
    params.limit = prList.totalResults;
    addSearchParams(params);

    const hasFilters = params.startDate || params.endDate || params.keyName || params.keyValue || params.dateKey;
    if (!hasFilters) {
      const { startDate, endDate } = getLast2MonthsDateRange();
      params.startDate = getStartOfDay(startDate, true);
      params.endDate = getEndOfDay(endDate, true);
      params.dateKey = "creationDate";
    }

    loadData(params, true);
  };

  useEffect(() => {
    loadData(filters);
  }, []);

  useEffect(() => {
    if (filterLabel === "category") getCategoryList();
    if (filterLabel === "site") getStoreList();
    if (filterLabel === "subCategory") getSubCategoryList();
    if (filterLabel === "itemId") getItemList();
    if (filterLabel === "department") getDepartmentList();
    if (filterLabel === "createdBy") getCreatorList();
  }, [filterLabel]);

  return (
    <div>
      <div className="finance-v2-reports-filter-con">
        <Grid container md={12} sx={{ mt: 2, display: "flex", alignItems: "center" }}>
          <Grid item md={2} sx={{ mr: 2 }}>
            <Autocomplete
              options={prFilters}
              renderInput={(params) => <TextField {...params} size="small" label="Select Filter" />}
              clearIcon={
                <ClearIcon
                  fontSize="small"
                  onClick={() => {
                    setFilterLabel("");
                    setFilterValue("");
                    loadData({ ...filters, page: 1 });
                  }}
                />
              }
              onChange={(e, val) => {
                if (val) {
                  setFilterValue("");
                  setFilterLabel(val.value);
                }
              }}
            />
          </Grid>

          {filterLabel && getFilterValue(filterLabel)}

          <Grid md={2} sx={{ ml: 2 }}>
            {filterLabel && (
              <Button
                variant="outlined"
                onClick={searchHandler}
                disabled={
                  !filterValue || (typeof filterValue === "object" && Object.keys(filterValue).length === 0)
                }
              >
                Search
              </Button>
            )}
          </Grid>
        </Grid>
        <DownloadBtn
          onClick={() => {
            downloadReport();
          }}
        />
      </div>

      {config.showTable && (
        <MuiTable
          columnsList={getPrHeadersConfig(config.approverHeaders)}
          dataList={prList.results || []}
          filters={filters}
          maxWords={3}
          pageCount={prList.totalPages}
          showAllFunc={setShowLargeText}
          onChange={(page) => {
            setFilters({ ...filters, page });
            const params = { ...filters, page };
            addSearchParams(params);
            loadData(params);
          }}
        />
      )}

      {showLargeText.isOpen && (
        <CustomModal onClose={() => setShowLargeText({ isOpen: false, text: "" })}>{showLargeText.text}</CustomModal>
      )}
    </div>
  );
};

export default PurchaseRequestReport;
